import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { v4 as uuidv4 } from "uuid";

type LikedName = {
  id: string;
  nombre: string;
  significado_resumido: string;
  significado_extendido: string;
  ranking: string;
  personajes_famosos?: Array<string>;
  sex: "male" | "female";
  country: string;
};

type LikedNamesContextType = {
  likedNames: LikedName[];
  addLikedName: (
    name: Omit<LikedName, "id" | "sex" | "country">,
    sex: "male" | "female",
    country: string
  ) => void;
  removeLikedName: (name: string) => void;
  removeLikedNameExact: (name: string, sex: "male" | "female", country: string) => void;
  reorderLikedNames: (newOrder: LikedName[]) => void;
  clearLikedNames: () => void;
};

const LikedNamesContext = createContext<LikedNamesContextType | undefined>(
  undefined
);

export const LikedNamesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [likedNames, setLikedNames] = useState<LikedName[]>([]);

  const addLikedName = useCallback(
    (
      name: Omit<LikedName, "id" | "sex" | "country">,
      sex: "male" | "female",
      country: string
    ) => {
      setLikedNames((prev) => {
        const exists = prev.some(
          (likedName) =>
            likedName.nombre === name.nombre &&
            likedName.sex === sex &&
            likedName.country === country
        );
        if (exists) return prev;

        return [
          ...prev,
          {
            ...name,
            id: uuidv4(),
            sex,
            country,
          },
        ];
      });
    },
    []
  );

  const reorderLikedNames = useCallback((newOrder: LikedName[]) => {
    setLikedNames([...newOrder]);
  }, []);

  const clearLikedNames = useCallback(() => {
    setLikedNames([]);
  }, []);

  const removeLikedName = useCallback((name: string) => {
    setLikedNames((prev) => prev.filter((item) => item.nombre !== name));
  }, []);

  const removeLikedNameExact = useCallback((name: string, sex: "male" | "female", country: string) => {
    setLikedNames((prev) => 
      prev.filter((item) => 
        !(item.nombre === name && item.sex === sex && item.country === country)
      )
    );
  }, []);

  const contextValue = useMemo(
    () => ({
      likedNames,
      addLikedName,
      removeLikedName,
      removeLikedNameExact,
      reorderLikedNames,
      clearLikedNames,
    }),
    [
      likedNames,
      addLikedName,
      reorderLikedNames,
      clearLikedNames,
      removeLikedName,
      removeLikedNameExact,
    ]
  );

  return (
    <LikedNamesContext.Provider value={contextValue}>
      {children}
    </LikedNamesContext.Provider>
  );
};

export const useLikedNames = (): LikedNamesContextType => {
  const context = useContext(LikedNamesContext);
  if (!context) {
    throw new Error("useLikedNames must be used within a LikedNamesProvider");
  }
  return context;
};
